/**
 * Product buy box: the product page (sections/main-product.liquid) and the quick view popup
 * (sections/quick-view.liquid, assets/quick-view.js), both marked [data-product-root].
 *
 * Option pickers -> the matching variant -> price, compare-at price, SKU, sold-out state, the
 * stock ceiling for the quantity stepper, and the data-variant-id / data-quantity attributes
 * that assets/cart-sidebar.js reads when Add To Cart or Buy Now is clicked. Adding to the cart
 * itself lives in cart-sidebar.js; nothing here talks to the server.
 *
 * - data-update-url="false" on the root: don't write ?variant= into the address (the quick view
 *   is not the product's page).
 * - Every choice fires `product:variant-change` on the root, detail { variant } (null when the
 *   combination doesn't exist), for anything else that follows the variant (photos, stock text).
 * - window.BEProductBuyBox.init(root) starts a buy box that arrives after page load.
 */
(function () {
	'use strict';

	function all(context, selector) {
		return Array.prototype.slice.call(context.querySelectorAll(selector));
	}

	function init(root) {
		if (root.hasAttribute('data-buy-box-ready')) return;
		var form = root.querySelector('[data-product-form]');
		var json = root.querySelector('[data-product-variants]');
		if (!form || !json) return;

		var variants;
		try {
			variants = JSON.parse(json.textContent);
		} catch (error) {
			return;
		}
		root.setAttribute('data-buy-box-ready', '');

		var addButton = form.querySelector('[data-add-to-cart]');
		var buyButton = form.querySelector('[data-buy-now]');
		var buttons = [addButton, buyButton].filter(Boolean);
		var addLabel = addButton ? addButton.querySelector('[data-add-label]') : null;
		var minus = form.querySelector('[data-product-qty-minus]');
		var plus = form.querySelector('[data-product-qty-plus]');
		var quantityValue = form.querySelector('[data-product-qty-value]');
		var price = root.querySelector('[data-product-price]');
		var currentPrice = root.querySelector('[data-product-current-price]');
		var comparePrice = root.querySelector('[data-product-compare-price]');
		var sku = root.querySelector('[data-product-sku]');
		// inline buy boxes (snippets/inline-buy-box.liquid): live stock line and the chosen value
		// printed next to each option's name
		var stockLine = root.querySelector('[data-product-stock]');
		var optionLabels = all(root, '[data-option-selected]');
		var updateUrl = root.getAttribute('data-update-url') !== 'false';

		var labels = {
			add: form.getAttribute('data-label-add') || 'Add to cart',
			soldOut: form.getAttribute('data-label-sold-out') || 'Sold out',
			unavailable: form.getAttribute('data-label-unavailable') || 'Unavailable',
		};

		var initialId = addButton ? addButton.getAttribute('data-variant-id') : '';
		var current =
			variants.filter(function (variant) {
				return String(variant.id) === initialId;
			})[0] ||
			variants[0] ||
			null;
		var selected = current ? current.options.slice() : [];
		var quantity = 1;

		function maxQuantity() {
			return current && typeof current.max === 'number' ? current.max : Infinity;
		}

		function setQuantity(next) {
			var max = maxQuantity();
			quantity = Math.max(1, Math.min(next, Math.max(1, max)));
			if (quantityValue) quantityValue.textContent = quantity;
			if (minus) minus.disabled = quantity <= 1;
			if (plus) plus.disabled = quantity >= max;
			buttons.forEach(function (button) {
				button.setAttribute('data-quantity', quantity);
			});
		}

		function render(fromShopper) {
			var available = !!(current && current.available);

			buttons.forEach(function (button) {
				button.setAttribute('data-variant-id', current ? current.id : '');
				button.toggleAttribute('data-unavailable', !available);
				// cart-sidebar.js owns the button while its request is running
				if (!button.classList.contains('is-loading')) button.disabled = !available;
			});

			if (addLabel && addButton && !addButton.classList.contains('is-loading')) {
				addLabel.textContent = !current ? labels.unavailable : available ? labels.add : labels.soldOut;
			}

			if (stockLine) {
				stockLine.textContent = available ? 'In stock' : 'Out of stock';
				stockLine.classList.toggle('is-out', !available);
			}

			optionLabels.forEach(function (label) {
				var index = parseInt(label.getAttribute('data-option-selected'), 10) - 1;
				if (index >= 0 && selected[index]) label.textContent = selected[index];
			});

			if (current) {
				var onSale = !!current.compare_at_price;
				if (currentPrice) currentPrice.textContent = current.price;
				if (comparePrice) {
					comparePrice.textContent = onSale ? current.compare_at_price : '';
					comparePrice.hidden = !onSale;
				}
				if (price) {
					// the page's two original price looks — see the comment in main-product.liquid
					price.classList.toggle('text-gray-900', onSale);
					price.classList.toggle('text-gray-890', !onSale);
					price.style.color = onSale ? '#dbae32' : '';
					price.style.fontSize = onSale ? '34px' : '';
				}
				if (sku) {
					sku.textContent = current.sku || '';
					// the whole "SKU:" line goes when the variant has none
					if (sku.parentElement) sku.parentElement.hidden = !current.sku;
				}

				if (fromShopper && updateUrl && window.history && window.history.replaceState) {
					// Shopify's convention: ?variant= keeps the choice on refresh and in shared links.
					var url = new URL(window.location.href);
					url.searchParams.set('variant', current.id);
					window.history.replaceState(window.history.state, '', url.toString());
				}
			}

			setQuantity(quantity);
		}

		root.addEventListener('click', function (event) {
			var option = event.target instanceof Element ? event.target.closest('[data-option-value]') : null;
			if (!option) return;
			var group = option.closest('[data-option-group]');
			if (!group || !root.contains(group)) return;

			var position = parseInt(group.getAttribute('data-option-position'), 10) - 1;
			if (!(position >= 0)) return;

			event.preventDefault();
			selected[position] = option.getAttribute('data-option-value');
			all(group, '[data-option-value]').forEach(function (button) {
				var on = button === option;
				button.classList.toggle('is-selected', on);
				button.setAttribute('aria-pressed', on ? 'true' : 'false');
			});

			current =
				variants.filter(function (variant) {
					return variant.options.every(function (value, index) {
						return value === selected[index];
					});
				})[0] || null;
			render(true);
			root.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail: { variant: current } }));
		});

		// A request just finished: re-apply the label / disabled state for the current variant.
		form.addEventListener('cart:trigger-idle', function () {
			render(false);
		});

		if (minus) {
			minus.addEventListener('click', function () {
				setQuantity(quantity - 1);
			});
		}
		if (plus) {
			plus.addEventListener('click', function () {
				setQuantity(quantity + 1);
			});
		}

		render(false);
	}

	function start() {
		all(document, '[data-product-root]').forEach(init);
	}

	window.BEProductBuyBox = { init: init };

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start);
	} else {
		start();
	}
})();
